import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Toast from '../components/common/Toast';
import { apiService } from '../services/api';
import { authUtils } from '../services/auth';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import '../components/Auth/RegisterModal.css';
import './ManageCompetitionModals.css';

interface AdminAddUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AdminAddUsersModal: React.FC<AdminAddUsersModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  const [dropActive, setDropActive] = useState(false);
  const [bulkRole, setBulkRole] = useState<string>('student');
  const [note, setNote] = useState('');
  const [singleEmail, setSingleEmail] = useState('');

  const [bulkResult, setBulkResult] = useState<{ created: number; exists: number; invalid: number } | null>(null);

  const total = useMemo(() => {
    if (!bulkResult) return 0;
    return (bulkResult.created || 0) + (bulkResult.exists || 0) + (bulkResult.invalid || 0);
  }, [bulkResult]);

  // Start each opening with empty inputs, so nothing from an earlier visit counts as pending.
  useEffect(() => {
    if (!isOpen) return;
    setNote('');
    setSingleEmail('');
    setError('');
  }, [isOpen]);

  useEscapeToClose(onClose, {
    enabled: isOpen && !isLoading,
    confirmIfDirty: () => note.trim() !== '' || singleEmail.trim() !== '',
  });

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const parseEmailsFromText = (text: string): string[] => {
    // Accept one email per line, or comma/space separated.
    const tokens = text
      .split(/\r?\n|,|;|\s+/)
      .map((t) => t.trim())
      .filter(Boolean);

    // very light validation; backend also validates
    const emails = tokens.filter((t) => t.includes('@'));

    // Dedup
    return Array.from(new Set(emails.map((e) => e.toLowerCase())));
  };

  const handleEmailsFile = async (file: File) => {
    setBulkResult(null);
    setError('');

    const content = await file.text();
    const emails = parseEmailsFromText(content);
    if (emails.length === 0) {
      setError('No valid emails found in file');
      return;
    }

    setIsLoading(true);
    try {
      const token = authUtils.getStoredToken();
      const res = await apiService.post(
        '/users/bulk_create',
        {
          emails,
          role: bulkRole,
          note: note.trim() || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setBulkResult({
        created: (res.data?.created || []).length,
        exists: (res.data?.already_exists || []).length,
        invalid: (res.data?.invalid || []).length,
      });
      setToastMessage(`Bulk create complete (${emails.length} emails)`);
      onSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Bulk create failed';
      setError(String(msg));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSingle = async () => {
    const email = singleEmail.trim();
    setBulkResult(null);
    setError('');

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }

    setIsLoading(true);
    try {
      const token = authUtils.getStoredToken();
      const res = await apiService.post(
        '/users/bulk_create',
        {
          email,
          role: bulkRole,
          note: note.trim() || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setBulkResult({
        created: (res.data?.created || []).length,
        exists: (res.data?.already_exists || []).length,
        invalid: (res.data?.invalid || []).length,
      });
      setToastMessage(`User provisioned (${email})`);
      setSingleEmail('');
      onSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Create user failed';
      setError(String(msg));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    // The actual action is file-driven; keep submit for accessibility.
    e.preventDefault();
  };

  return (
    <>
      <div className="register-modal-backdrop" onClick={handleBackdropClick}>
        <div
          className="register-modal admin-addusers-modal"
          role="dialog"
          aria-modal="true"
          style={{ width: 'min(900px, 96vw)', maxWidth: '900px' }}
        >
          <div className="register-modal__header">
            <h2 className="register-modal__title">Add Users</h2>
            <button
              type="button"
              className="register-modal__close"
              onClick={onClose}
              aria-label="Close modal"
              disabled={isLoading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <form className="register-modal__form" onSubmit={handleSubmit}>
            {error && (
              <div className="register-modal__error" role="alert">
                {error}
              </div>
            )}

            <div className="register-modal__fields">
              <label className="input-wrapper">
                <span className="input__label">Role</span>
                <select
                  className="input input--default input--md"
                  value={bulkRole}
                  onChange={(e) => setBulkRole(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="student">student</option>
                  <option value="competitor">competitor</option>
                  <option value="host">host</option>
                  <option value="admin">admin</option>
                </select>
              </label>

              <Input
                name="note"
                label="Note (applied to all created users)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={isLoading}
                helperText="Optional. Stored in Users.note for each created user."
              />

              <div className="mcm-panel" style={{
                marginTop: '0.25rem',
                padding: '0.75rem',
                borderRadius: 12,
              }}>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'end', flexWrap: 'wrap' }}>
                  <Input
                    name="singleEmail"
                    label="Add single user (email)"
                    value={singleEmail}
                    onChange={(e) => setSingleEmail(e.target.value)}
                    disabled={isLoading}
                    placeholder="user@domain.com"
                    helperText="Creates one user immediately (no file upload needed)."
                  />
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={handleCreateSingle}
                    disabled={isLoading || !singleEmail.trim()}
                  >
                    Add User
                  </Button>
                </div>
              </div>

              <div className="mcm-text-secondary" style={{
                marginTop: '0.75rem',
                marginBottom: '-0.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                fontSize: '0.9rem',
              }}>
                <div className="mcm-divider" style={{ flex: 1, height: 1 }} />
                <span>or bulk upload</span>
                <div className="mcm-divider" style={{ flex: 1, height: 1 }} />
              </div>

              <div
                className={`admin-dropzone ${dropActive ? 'admin-dropzone--active' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropActive(true);
                }}
                onDragLeave={() => setDropActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDropActive(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) handleEmailsFile(f);
                }}
              >
                <strong>Drag & drop</strong> an emails file here, or{' '}
                <label className="mcm-link-label">
                  browse
                  <input
                    type="file"
                    accept=".txt,.csv"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleEmailsFile(f);
                      e.currentTarget.value = '';
                    }}
                    disabled={isLoading}
                  />
                </label>
                <div className="mcm-text-secondary" style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                  Accepted: .txt or .csv • one email per line (or comma/space-separated)
                </div>
              </div>

              {bulkResult && (
                <p className="mcm-text-secondary" style={{ marginTop: '0.5rem' }}>
                  Results (n={total}): created {bulkResult.created}, already existed {bulkResult.exists}, invalid {bulkResult.invalid}
                </p>
              )}
            </div>

            <div className="register-modal__actions">
              <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
                Close
              </Button>
            </div>
          </form>
        </div>
      </div>

      {toastMessage && <Toast message={toastMessage} duration={2500} onClose={() => setToastMessage('')} />}
    </>
  );
};

export default AdminAddUsersModal;
